import { useState } from "react";
import {
  Save, Trash2, X, Upload, Loader2, ShieldCheck, Star, ImagePlus,
} from "lucide-react";
import {
  updateAdminAnnonce,
  deleteAdminAnnonce,
  uploadToCloudinary,
} from "@/lib/api";
import { Section, Field } from "./FormBuildingBlocks";

export default function EditAnnonceModal({ annonce, onClose, onSaved, onDeleted }) {
  const [form, setForm] = useState({
    title: annonce.title || "",
    category: annonce.category || "",
    subcategory: annonce.subcategory || "",
    city: annonce.city || "",
    quartier: annonce.quartier || "",
    price: annonce.price || 0,
    description: annonce.description || "",
    image: annonce.image || "",
    images: annonce.images || [],
    tag: annonce.tag || "",
    premium: !!annonce.premium,
    verified: !!annonce.verified,
    phone: annonce.phone || "",
    whatsapp: annonce.whatsapp || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onUpload = async (e, target) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setError("");
    try {
      const urls = [];
      for (const f of files) {
        const u = await uploadToCloudinary(f);
        urls.push(u);
      }
      if (target === "main") update("image", urls[0]);
      else update("images", [...(form.images || []), ...urls]);
    } catch (err) {
      setError(err.message || "Echec de l'upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeGalleryImage = (idx) => {
    update("images", form.images.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const patch = { ...form, price: Number(form.price) || 0 };
      const updated = await updateAdminAnnonce(annonce.id, patch);
      onSaved(updated);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!window.confirm(`Supprimer definitivement l'annonce "${annonce.title}" (#${annonce.id}) ?`)) return;
    setSaving(true);
    try {
      await deleteAdminAnnonce(annonce.id);
      onDeleted(annonce.id);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Erreur");
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="admin-edit-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
      >
        <div className="sticky top-0 bg-white border-b border-[var(--line)] px-6 py-4 flex items-center justify-between rounded-t-3xl">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-orange-600">Annonce #{annonce.id}</div>
            <div className="font-bold text-lg truncate">{form.title || "(sans titre)"}</div>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-orange-50" data-testid="admin-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <Section title="Image principale">
            <div className="flex items-start gap-4">
              <img
                src={form.image || "/placeholder.png"}
                alt=""
                className="w-32 h-32 rounded-2xl object-cover bg-orange-50 border border-[var(--line)]"
                data-testid="admin-main-image"
              />
              <div className="flex-1 space-y-2">
                <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer" data-testid="admin-upload-main">
                  <Upload size={14} /> {uploading ? "Upload..." : "Changer l'image"}
                  <input type="file" accept="image/*" hidden onChange={(e) => onUpload(e, "main")} disabled={uploading} />
                </label>
                <input
                  type="text"
                  value={form.image}
                  onChange={(e) => update("image", e.target.value)}
                  placeholder="ou URL directe..."
                  className="w-full px-3 py-2 rounded-lg border border-[var(--line)] text-xs"
                  data-testid="admin-image-url"
                />
              </div>
            </div>
          </Section>

          <Section title="Galerie (photos supplementaires)">
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {(form.images || []).map((url, i) => (
                <div key={`${url}-${i}`} className="relative group">
                  <img src={url} alt="" className="w-full h-20 object-cover rounded-xl bg-orange-50" />
                  <button
                    type="button"
                    onClick={() => removeGalleryImage(i)}
                    className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-red-500 text-white grid place-items-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`admin-remove-image-${i}`}
                    aria-label="Retirer"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              <label
                className="border-2 border-dashed border-[var(--line)] rounded-xl h-20 grid place-items-center text-[var(--ink-soft)] hover:border-orange-300 hover:text-orange-600 cursor-pointer text-xs"
                data-testid="admin-upload-gallery"
              >
                {uploading ? <Loader2 className="animate-spin" size={16} /> : <ImagePlus size={16} />}
                <input type="file" accept="image/*" multiple hidden onChange={(e) => onUpload(e, "gallery")} disabled={uploading} />
              </label>
            </div>
          </Section>

          <Section title="Informations">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Titre" value={form.title} onChange={(v) => update("title", v)} testid="admin-field-title" />
              <Field label="Prix (FCFA)" type="number" value={form.price} onChange={(v) => update("price", v)} testid="admin-field-price" />
              <Field label="Categorie" value={form.category} onChange={(v) => update("category", v)} testid="admin-field-category" />
              <Field label="Sous-categorie" value={form.subcategory} onChange={(v) => update("subcategory", v)} testid="admin-field-subcategory" />
              <Field label="Ville" value={form.city} onChange={(v) => update("city", v)} testid="admin-field-city" />
              <Field label="Quartier" value={form.quartier} onChange={(v) => update("quartier", v)} testid="admin-field-quartier" />
              <Field label="Telephone" value={form.phone} onChange={(v) => update("phone", v)} testid="admin-field-phone" />
              <Field label="WhatsApp" value={form.whatsapp} onChange={(v) => update("whatsapp", v)} testid="admin-field-whatsapp" />
              <Field label="Tag (ex: Recente)" value={form.tag} onChange={(v) => update("tag", v)} testid="admin-field-tag" />
            </div>
            <div className="mt-3">
              <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--ink-soft)] mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => update("description", e.target.value)}
                rows={4}
                className="w-full px-3 py-2 rounded-xl border border-[var(--line)] text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                data-testid="admin-field-description"
              />
            </div>
            <div className="flex gap-3 mt-3">
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.premium}
                  onChange={(e) => update("premium", e.target.checked)}
                  data-testid="admin-field-premium"
                />
                <Star size={14} className="text-orange-500" /> Premium
              </label>
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.verified}
                  onChange={(e) => update("verified", e.target.checked)}
                  data-testid="admin-field-verified"
                />
                <ShieldCheck size={14} className="text-green-600" /> Verifie
              </label>
            </div>
          </Section>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-xl" data-testid="admin-error">
              {error}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[var(--line)] px-6 py-4 flex justify-between gap-3 rounded-b-3xl">
          <button
            type="button"
            onClick={confirmDelete}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-red-600 border border-red-200 hover:bg-red-50 disabled:opacity-50"
            data-testid="admin-delete-btn"
          >
            <Trash2 size={14} /> Supprimer
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-secondary" data-testid="admin-cancel-btn">
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving || uploading}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="admin-save-btn"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Sauvegarde..." : "Enregistrer"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
